<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { stockDailyApi, tradeCalendarApi } from '@/api/stock'
import { useTableSort } from '@/composables/useTableSort'
import { toast } from '@/composables/useToast'
import StockFilterPanel from '@/components/stock/StockFilterPanel.vue'
import StockDataTable from '@/components/stock/StockDataTable.vue'
import BasePagination from '@/components/common/BasePagination.vue'
import PageHero from '@/components/common/PageHero.vue'

const router = useRouter()
const route = useRoute()
const loading = ref(false)
const stockList = ref([])
const total = ref(0)
const pageNum = ref(1)
const pageSize = ref(10)
const latestTradeDate = ref('')
const filterForm = ref({ tradeDate: '', symbol: '' })
const startDate = ref('')
const endDate = ref('')
const priceField = ref('fit')
const smallSwingFilter = ref(true)
const layer3Cache = ref(null)
const layer3Key = ref('')
let layer3Pending = null
let layer3PendingKey = ''
const layer3Summary = computed(() => {
  if (!smallSwingFilter.value || !layer3Cache.value ||
      layer3Key.value !== `${startDate.value}|${endDate.value}|10|3`) return ''
  return `小振幅排除 ${layer3Cache.value.counts.excluded} 只 · 峰谷下降排除 ${layer3Cache.value.counts.declining || 0} 只 · 证据不足排除 ${layer3Cache.value.counts.insufficient} 只`
})
let requestSequence = 0
const { sortField, sortOrder, handleSort } = useTableSort({
  defaultField: 'symbol',
  defaultOrder: 'asc',
  onChange: () => loadData()
})

function daysBefore (isoDate, days) {
  const value = new Date(`${isoDate}T00:00:00`)
  value.setDate(value.getDate() - days)
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
}

function mapStock (item) {
  return {
    id: item.id,
    symbol: item.symbol,
    name: item.name,
    tradeDate: item.trade_date,
    open: item.open,
    high: item.high,
    low: item.low,
    close: item.close,
    priceChange: item.price_change,
    pctChg: item.pct_chg,
    amplitude: item.amplitude,
    volume: item.volume,
    amount: item.amount,
    turnover: item.turnover
  }
}

function currentLayer3Key () {
  return `${startDate.value}|${endDate.value}|10|3`
}

async function ensureLayer3 () {
  const key = currentLayer3Key()
  if (key === layer3Key.value && layer3Cache.value) return layer3Cache.value
  if (key === layer3PendingKey && layer3Pending) return layer3Pending
  const pending = stockDailyApi.getVolatilityLayer3({
    start_date: startDate.value,
    end_date: endDate.value,
    max_leg_pct: 10,
    consecutive_legs: 3
  })
  layer3Pending = pending
  layer3PendingKey = key
  try {
    const result = await pending
    if (key === currentLayer3Key()) {
      layer3Cache.value = result
      layer3Key.value = key
    }
    return result
  } finally {
    if (layer3Pending === pending) {
      layer3Pending = null
      layer3PendingKey = ''
    }
  }
}

async function loadData () {
  const requestId = ++requestSequence
  loading.value = true
  try {
    let symbols
    if (smallSwingFilter.value) {
      if (!startDate.value || !endDate.value || startDate.value > endDate.value) {
        toast.warning('请选择有效的极值起止日期')
        return
      }
      const layer3 = await ensureLayer3()
      if (requestId !== requestSequence) return
      symbols = layer3?.passed_symbols || []
    }
    const result = await stockDailyApi.getVolatilityUniverse({
      trade_date: filterForm.value.tradeDate || undefined,
      symbol: filterForm.value.symbol.trim() || undefined,
      symbols,
      page_num: pageNum.value,
      page_size: pageSize.value,
      sort_field: sortField.value,
      sort_order: sortOrder.value
    })
    if (requestId !== requestSequence) return
    stockList.value = (result?.list || []).map(mapStock)
    total.value = result?.total || 0
    if (result?.trade_date) {
      filterForm.value.tradeDate = result.trade_date
      if (!endDate.value) {
        endDate.value = result.trade_date
        startDate.value = daysBefore(endDate.value, 90)
      }
    }
  } catch (error) {
    if (requestId !== requestSequence) return
    toast.error(error.message || '加载股票列表失败')
    stockList.value = []
    total.value = 0
  } finally {
    if (requestId === requestSequence) loading.value = false
  }
}

function handleSmallSwingFilterChange () {
  if (smallSwingFilter.value) priceField.value = 'fit'
  pageNum.value = 1
  loadData()
}

async function handleSearch () {
  if (!startDate.value || !endDate.value || startDate.value > endDate.value) {
    toast.warning('请选择有效的极值起止日期')
    return
  }
  pageNum.value = 1
  await loadData()
}

async function handleReset () {
  filterForm.value = { tradeDate: latestTradeDate.value, symbol: '' }
  endDate.value = latestTradeDate.value
  startDate.value = daysBefore(endDate.value, 90)
  priceField.value = 'fit'
  smallSwingFilter.value = true
  sortField.value = 'symbol'
  sortOrder.value = 'asc'
  pageNum.value = 1
  await loadData()
}

function handleRowDblClick (item) {
  if (!item?.symbol) return
  if (!startDate.value || !endDate.value || startDate.value > endDate.value) {
    toast.warning('请先选择有效的极值起止日期')
    return
  }
  router.push({
    name: 'stock-kline',
    params: { symbol: item.symbol },
    query: {
      name: item.name,
      extrema_start: startDate.value,
      extrema_end: endDate.value,
      extrema_field: priceField.value
    }
  })
}

onMounted(async () => {
  try {
    latestTradeDate.value = await tradeCalendarApi.getLatest() || ''
  } catch (error) {
    toast.error(error.message || '获取最近交易日失败')
  }
  filterForm.value.tradeDate = String(route.query.trade_date || latestTradeDate.value)
  endDate.value = String(route.query.end || latestTradeDate.value)
  startDate.value = String(route.query.start || (endDate.value ? daysBefore(endDate.value, 90) : ''))
  const requestedPriceField = route.query.price_field
  if (requestedPriceField === 'close') {
    priceField.value = 'fit'
  } else if (['fit', 'range', 'open', 'high', 'low', 'raw_close'].includes(requestedPriceField)) {
    priceField.value = requestedPriceField
  } else {
    priceField.value = 'fit'
  }
  if (smallSwingFilter.value) priceField.value = 'fit'
  await loadData()
})
</script>

<template>
  <div class="volatility-universe page">
    <PageHero />
    <StockFilterPanel
      v-model="filterForm"
      :show-turnover="false"
      :show-amount="false"
      :show-board-filters="false"
      @search="handleSearch"
      @reset="handleReset"
    />
    <div class="stage-controls">
      <span class="stage-title">极值观察区间</span>
      <label>起始日 <input v-model="startDate" type="date" /></label>
      <label>结束日 <input v-model="endDate" type="date" /></label>
      <label>价格 <select v-model="priceField" :disabled="smallSwingFilter">
        <option value="fit">拟合收盘价转折点</option>
        <option value="range">日内最高价 / 最低价</option>
        <option value="raw_close">原始收盘价最大 / 最小</option>
        <option value="high">最高价</option>
        <option value="low">最低价</option>
        <option value="open">开盘价</option>
      </select></label>
      <span class="stage-hint">勾选时按 K 线上标出的拟合峰谷筛选；双击股票可核对</span>
      <label class="swing-filter"><input v-model="smallSwingFilter" type="checkbox" @change="handleSmallSwingFilterChange" />过滤最近 3 段均 &lt; 10% 或峰谷持续降低</label>
      <span class="stage-hint">{{ layer3Summary }}</span>
      <span class="stage-count">{{ total.toLocaleString('zh-CN') }} 只</span>
    </div>
    <StockDataTable
      :list="stockList"
      :sort-field="sortField"
      :sort-order="sortOrder"
      :loading="loading"
      @sort="handleSort"
      @row-dblclick="handleRowDblClick"
    />
    <BasePagination
      v-if="total > 0"
      :page-num="pageNum"
      :page-size="pageSize"
      :total="total"
      @update:page-num="pageNum = $event; loadData()"
      @update:page-size="pageSize = $event; pageNum = 1; loadData()"
    />
  </div>
</template>

<style scoped lang="scss">
.volatility-universe {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 4px 28px 18px;
  box-sizing: border-box;
  overflow: hidden;
}

.stage-controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 18px;
  padding: 12px 0;
  border-bottom: 1px solid var(--rule);
  color: var(--text-secondary);
  font-size: 12px;

  label { display: inline-flex; align-items: center; gap: 7px; }
  input, select {
    padding: 5px 8px;
    background: transparent;
    border: 1px solid var(--rule);
    border-radius: 4px;
    color: var(--text-primary);
    font: inherit;
  }
  .stage-title { color: var(--text-primary); font-weight: 600; }
  .swing-filter { color: var(--text-primary); cursor: pointer; }
  .swing-filter input { width: 14px; height: 14px; margin: 0; accent-color: var(--accent); }
  .stage-hint { color: var(--text-faint); }
  .stage-count { margin-left: auto; font-family: var(--font-mono); color: var(--accent); }
}

@media (max-width: 768px) {
  .volatility-universe { height: auto; min-height: 100%; overflow: visible; padding: 4px 16px 12px; }
  .stage-count { margin-left: 0; }
}
</style>
